import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { string } from 'prop-types';
import { DayPickerRangeController } from 'react-dates';
import classNames from 'classnames';
import moment from 'moment';
import { START_DATE } from '../../util/dates';
import { IconArrowHead } from '../../components';
import css from './DateRangeController.module.css';

export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const DateRangeController = forwardRef((props, ref) => {
  const {
      rootClassName = null,
      className = null,
      _startDateOffset = undefined,
      _endDateOffset = undefined,
      _orientation = HORIZONTAL_ORIENTATION,
      _verticalHeight = undefined,
      _withPortal = false,
      _isRTL = false,
      _initialVisibleMonth = null,
      _firstDayOfWeek = 0,
      _numberOfMonths = 1,
      _daySize = 38,
      _keepOpenOnDateSelect = false,
      _renderCalendarInfo = null,
      _hideKeyboardShortcutsPanel = true,
      _navPrev = <PrevIcon />,
      _navNext = <NextIcon />,
      _onPrevMonthClick = () => {},
      _onNextMonthClick = () => {},
      _transitionDuration = 200,
      _renderCalendarDay = undefined,
      _renderDayContents = day => <span className="renderedDay">{day.format('D')}</span>,
      _minimumNights = 0,
      _enableOutsideDays = false,
      _isDayBlocked = () => false,
      _isOutsideRange = () => false,
      _isDayHighlighted = () => {},
      _monthFormat = 'MMMM YYYY',
      _weekDayFormat = 'dd',

      name,
      value,
      onChange,
      onFocus,
      meta,
      children,
      render,
      ...controllerProps
  } = props;

  const [startDate, setStartDate] = useState(
    value && value.startDate ? moment(value.startDate) : null
  );
  const [endDate, setEndDate] = useState(value && value.endDate ? moment(value.endDate) : null);
  const [focusedInput, setFocusedInput] = useState(START_DATE);

  const onDatesChange = useCallback(
    ({ startDate: newStart, endDate: newEnd }) => {
      const start = newStart ? newStart.toDate() : null;
      const end = newEnd ? newEnd.toDate() : null;
      setStartDate(newStart);
      setEndDate(newEnd);
      if (newStart && newEnd) {
        onChange({ startDate: start, endDate: end });
      }
    },
    [onChange]
  );

  const onFocusChange = useCallback(input => {
    setFocusedInput(!input ? START_DATE : input);
  }, []);

  const onReset = useCallback((resetStart, resetEnd) => {
    if (resetStart && resetEnd) {
      setStartDate(moment(resetStart));
      setEndDate(moment(resetEnd));
      setFocusedInput(START_DATE);
    } else {
      setStartDate(null);
      setEndDate(null);
      setFocusedInput(START_DATE);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    onReset,
  }));

  const classes = classNames(rootClassName || css.inputRoot, className);

  const startDateFromForm = value && value.startDate ? moment(value.startDate) : null;
  const endDateFromForm = value && value.endDate ? moment(value.endDate) : null;
  const isSelected = startDate && endDate;
  const start = isSelected ? startDateFromForm : startDate;
  const end = isSelected ? endDateFromForm : endDate;

  return (
    <div className={classes}>
      <DayPickerRangeController
        {...controllerProps}
        startDate={start}
        endDate={end}
        onDatesChange={onDatesChange}
        focusedInput={focusedInput}
        onFocusChange={onFocusChange}
      />
    </div>
  );
});

DateRangeController.propTypes = {
  rootClassName: string,
  className: string,
};

export default DateRangeController;
